import { skipToken, useSuspenseQuery } from "@apollo/client/react";
import { isBefore, parseISO, subHours } from "date-fns";
import { Eye, FlaskConical, MoreHorizontal, Pencil, Play, RefreshCw } from "lucide-react";
import { type ReactNode, Suspense, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BatchChangeRequestsJobType, type DryRunOptions, type JobLogLevel } from "~/common/types";
import type { JobRun } from "~/components/job-log";
import { DetailLoading } from "~/components/loading";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "~/components/ui";
import { useDipLifecycle } from "~/features/dataImportProjects/context";
import {
  GetBatchChangeRequestsJobDocument,
  type GetBatchChangeRequestsJobQuery,
  type GetBatchChangeRequestsJobQueryVariables,
  GetBatchChangeRequestsJobRunResultDocument,
  type GetBatchChangeRequestsJobRunResultQuery,
  type GetBatchChangeRequestsJobRunResultQueryVariables,
  GetBatchChangeRequestsJobsDocument,
} from "~/features/dataImportProjects/gqls";
import { useBatchChangeRequestsJobActions } from "~/features/dataImportProjects/hooks/useBatchChangeRequestsJobActions";
import { useDipResourceMutationGuard } from "~/features/dataImportProjects/hooks/useDipResourceMutationGuard";
import { useSubmitBatchChangeRequestsJob } from "~/features/dataImportProjects/hooks/useSubmitBatchChangeRequestsJob";
import type { DipPeekTarget, DipPeekType } from "~/features/dataImportProjects/types";
import { evidenceSetsOfBatchChangeRequestsJob } from "~/features/dataImportProjects/types";
import { useTenant } from "~/features/tenant/context";
import { cn } from "~/lib/utils";
import { rootRoute as R } from "~/routes";
import { BatchChangeRequestsJobDryRunResultDialog } from "./BatchChangeRequestsJobDryRunResultDialog";
import { BatchChangeRequestsJobForm } from "./BatchChangeRequestsJobForm";
import { BatchChangeRequestsJobLogDialog } from "./BatchChangeRequestsJobLogDialog";
import { BatchChangeRequestsPanel } from "./BatchChangeRequestsPanel";
import { DipReadOnlyTooltip } from "./DipReadOnlyTooltip";

type BatchChangeRequestsJobQueryRow = GetBatchChangeRequestsJobQuery["batchChangeRequestsJobs"][number];
type BatchChangeRequestsJobRun = BatchChangeRequestsJobQueryRow["batchChangeRequestsJobRuns"][number];
type BatchChangeRequestsJob = Omit<BatchChangeRequestsJobQueryRow, "batchChangeRequestsJobRuns"> & {
  batchChangeRequestsJobRuns: (Omit<BatchChangeRequestsJobRun, "user"> & {
    user: BatchChangeRequestsJobRun["user"] | null;
  })[];
};

type BatchChangeRequestsJobDetailPanelProps = {
  jobId: string;
  mode?: "peek" | "page";
  initialTab?: "info" | "changeRequests";
  recordComposerDefinitionId?: string;
  /** Open another peek (e.g. a composer) from the change-requests table. Only available in peek mode. */
  onOpenPeek?: (target: DipPeekTarget) => void;
};

type MetadataRowProps = {
  label: string;
  children: ReactNode;
};

function MetadataRow({ label, children }: MetadataRowProps) {
  return (
    <div className="grid grid-cols-[140px_minmax(0,1fr)] gap-3">
      <dt className="text-12-regular text-fg-3">{label}</dt>
      <dd className="min-w-0 text-14-regular text-fg-1">{children}</dd>
    </div>
  );
}

function formatDate(t: ReturnType<typeof useTranslation>["t"], iso: string | null | undefined) {
  return iso ? t("date.short", { date: parseISO(iso) }) : "-";
}

function DryRunOptionsDialog({
  open,
  onOpenChange,
  onConfirm,
  readOnly,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (options: DryRunOptions, logLevel?: JobLogLevel) => void;
  readOnly: boolean;
}) {
  const { t } = useTranslation();
  const [limit, setLimit] = useState("0");
  const [offset, setOffset] = useState("0");
  const [logLevel, setLogLevel] = useState<JobLogLevel>("info");

  const confirm = () => {
    if (readOnly) return;
    onConfirm(
      {
        limit: limit ? Number(limit) || undefined : undefined,
        offset: offset ? Number(offset) || undefined : undefined,
      },
      logLevel !== "info" ? logLevel : undefined,
    );
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("feature.dataImportProjects.dryRunOptions.title")}</DialogTitle>
          <DialogDescription>{t("feature.dataImportProjects.dryRunOptions.description")}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="grid gap-2">
            <Label htmlFor="bcr-job-dry-run-limit">{t("ui.limit")}</Label>
            <Input
              id="bcr-job-dry-run-limit"
              inputMode="numeric"
              value={limit}
              onChange={(event) => setLimit(event.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="bcr-job-dry-run-offset">{t("ui.offset")}</Label>
            <Input
              id="bcr-job-dry-run-offset"
              inputMode="numeric"
              value={offset}
              onChange={(event) => setOffset(event.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <Label>{t("ui.logLevel")}</Label>
            <Select value={logLevel} onValueChange={(value) => setLogLevel(value as JobLogLevel)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="info">{t("ui.logLevelInfo")}</SelectItem>
                <SelectItem value="debug">{t("ui.logLevelDebug")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            {t("ui.cancel")}
          </Button>
          <Button type="button" onClick={confirm}>
            {t("feature.dataImportProjects.dryRunOptions.runDryRun")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RunJobDialog({
  open,
  onOpenChange,
  onConfirm,
  readOnly,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (logLevel?: JobLogLevel) => void;
  readOnly: boolean;
}) {
  const { t } = useTranslation();
  const [logLevel, setLogLevel] = useState<JobLogLevel>("info");

  const confirm = () => {
    if (readOnly) return;
    onConfirm(logLevel !== "info" ? logLevel : undefined);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("feature.dataImportProjects.detail.operation.runJob")}</DialogTitle>
          <DialogDescription>
            {t("feature.dataImportProjects.detail.operation.runJobConfirmDescription")}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-2">
          <Label>{t("ui.logLevel")}</Label>
          <Select value={logLevel} onValueChange={(value) => setLogLevel(value as JobLogLevel)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="info">{t("ui.logLevelInfo")}</SelectItem>
              <SelectItem value="debug">{t("ui.logLevelDebug")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            {t("ui.cancel")}
          </Button>
          <Button type="button" onClick={confirm}>
            {t("feature.dataImportProjects.detail.operation.runJob")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BatchChangeRequestsJobEditBridge({
  open,
  onOpenChange,
  job,
  importProjectId,
  evidenceSetId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  job: BatchChangeRequestsJob;
  importProjectId: string;
  evidenceSetId: string;
}) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {t("ui.edit")} {t("model.batchChangeRequestsJob.modelName")}
          </DialogTitle>
          <DialogDescription>
            {t("feature.dataImportProjects.v2.temporaryLegacyBridge", {
              defaultValue: "Temporary legacy component bridge",
            })}
          </DialogDescription>
        </DialogHeader>
        <Suspense fallback={<DetailLoading />}>
          <BatchChangeRequestsJobForm
            batchChangeRequestsJobId={job.id}
            onSubmit={() => onOpenChange(false)}
            refetchQueriesOnEdit={[
              {
                query: GetBatchChangeRequestsJobsDocument,
                variables: { evidenceSetId },
              },
            ]}
            importProjectId={importProjectId}
            evidenceSetId={evidenceSetId}
          />
        </Suspense>
      </DialogContent>
    </Dialog>
  );
}

function ChangeRequestsCount({ tenantId, jobRunId }: { tenantId: string; jobRunId: string }) {
  const { data } = useSuspenseQuery<
    GetBatchChangeRequestsJobRunResultQuery,
    GetBatchChangeRequestsJobRunResultQueryVariables
  >(GetBatchChangeRequestsJobRunResultDocument, {
    variables: {
      tenantId,
      jobRunId,
    },
  });

  const count = data?.batchChangeRequestsJobRuns?.[0]?.changeRequestsAggregate.aggregate?.count;
  return <>{typeof count === "number" ? count : "-"}</>;
}

function JobRunsTable({
  job,
  onOpenLog,
  onMarkFailed,
  readOnly,
}: {
  job: BatchChangeRequestsJob;
  onOpenLog: (jobRun: JobRun) => void;
  onMarkFailed: (jobRunId: string) => void;
  readOnly: boolean;
}) {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  const shouldFetchCountIdx = job.batchChangeRequestsJobRuns.findIndex((jobRun) => jobRun.statusId === "succeeded");

  if (!tenantId) {
    throw new Error("Tenant ID is required");
  }

  if (job.batchChangeRequestsJobRuns.length === 0) {
    return (
      <p className="rounded-[var(--radius-md)] border border-border-default p-4 text-12-regular text-fg-3">
        {t("feature.common.noData")}
      </p>
    );
  }

  return (
    <div className="overflow-x-auto rounded-[var(--radius-md)] border border-border-default">
      <table className="w-full min-w-[820px] border-collapse text-12-regular">
        <thead className="bg-bg-subtle text-fg-2">
          <tr>
            <th className="border-b border-border-default px-3 py-2 text-left font-semibold">
              {t("model.common.createdAt")}
            </th>
            <th className="border-b border-border-default px-3 py-2 text-left font-semibold">
              {t("model.batchChangeRequestsJobRun.runStatusId")}
            </th>
            <th className="border-b border-border-default px-3 py-2 text-right font-semibold">
              {t("model.batchChangeRequestsJobRun.changeRequestsCount")}
            </th>
            <th className="border-b border-border-default px-3 py-2 text-left font-semibold">
              {t("model.batchChangeRequestsJobRun.user")}
            </th>
            <th className="border-b border-border-default px-3 py-2 text-left font-semibold">{t("ui.actions")}</th>
          </tr>
        </thead>
        <tbody>
          {job.batchChangeRequestsJobRuns.map((jobRun, index) => {
            const canMarkFailed =
              jobRun.statusId === "init" && isBefore(parseISO(jobRun.createdAt), subHours(new Date(), 2));
            return (
              <tr key={jobRun.id} className="border-t border-border-default first:border-t-0">
                <td className="px-3 py-2 text-fg-2">{t("date.long", { date: parseISO(jobRun.createdAt) })}</td>
                <td className="px-3 py-2 text-fg-1">{jobRun.statusId}</td>
                <td className="px-3 py-2 text-right text-fg-2">
                  {index === shouldFetchCountIdx ? (
                    <Suspense fallback={<Skeleton className="ml-auto inline-block h-3 w-6" />}>
                      <ChangeRequestsCount tenantId={tenantId} jobRunId={jobRun.id} />
                    </Suspense>
                  ) : (
                    "-"
                  )}
                </td>
                <td className="px-3 py-2 text-fg-2">{jobRun.user?.email ?? "—"}</td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        onOpenLog({
                          jobRunId: jobRun.id,
                          s3LogPath: jobRun.s3LogPath,
                          jobName: "BatchChangeRequestsJob",
                        })
                      }
                    >
                      <Eye className="size-3.5" />
                      {t("ui.view")}
                    </Button>
                    {canMarkFailed ? (
                      <DipReadOnlyTooltip disabled={readOnly} ariaLabel={t("ui.markAsFailed")}>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={readOnly}
                          onClick={() => {
                            if (readOnly) return;
                            onMarkFailed(jobRun.id);
                          }}
                        >
                          {t("ui.markAsFailed")}
                        </Button>
                      </DipReadOnlyTooltip>
                    ) : null}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function BatchChangeRequestsJobInfo({
  job,
  onOpenPeek,
}: {
  job: BatchChangeRequestsJob;
  onOpenPeek?: (target: DipPeekTarget) => void;
}) {
  const { t } = useTranslation();
  const composerDefs = job.batchChangeRequestsJobRecordComposerDefinitions.map((def) => def.recordComposerDefinition);

  return (
    <section className="grid gap-6 lg:grid-cols-[minmax(260px,0.85fr)_minmax(0,1.15fr)]">
      <dl className="grid gap-3">
        <MetadataRow label={t("model.common.name")}>{job.name || `(${t("ui.noName")})`}</MetadataRow>
        <MetadataRow label={t("ui.type")}>{job.jobTypeId}</MetadataRow>
        {job.deactivatedAt ? (
          <MetadataRow label={t("model.common.deactivatedAt")}>
            <span className="text-score-c">{formatDate(t, job.deactivatedAt)}</span>
          </MetadataRow>
        ) : null}
        {(!job.jobTypeId || job.jobTypeId === BatchChangeRequestsJobType.S3Script) && (
          <MetadataRow label={t("model.batchChangeRequestsJob.s3Path")}>{job.s3Path || "-"}</MetadataRow>
        )}
      </dl>

      <dl className="grid gap-3">
        <MetadataRow label={t("model.recordComposer.modelNames")}>
          {composerDefs.length > 0 ? (
            <ul className="grid gap-2">
              {composerDefs.map((composer) => {
                const type: DipPeekType = composer.entityComposerDefinition ? "entityComposer" : "relationComposer";
                return (
                  <li key={composer.id} className="flex flex-wrap items-center gap-2">
                    {composer.deactivatedAt ? <Badge tone="status-draft">{t("ui.deactivated")}</Badge> : null}
                    {onOpenPeek ? (
                      <button
                        type="button"
                        className="text-left text-primary-700 hover:underline"
                        onClick={() => onOpenPeek({ type, id: composer.id })}
                      >
                        {composer.name}
                      </button>
                    ) : (
                      <span>{composer.name}</span>
                    )}
                  </li>
                );
              })}
            </ul>
          ) : (
            "-"
          )}
        </MetadataRow>
      </dl>
    </section>
  );
}

function BatchChangeRequestsJobDetailPanelContent({
  job,
  refetch,
  mode,
  initialTab,
  recordComposerDefinitionId,
  onOpenPeek,
  canExecuteMutation,
}: {
  job: BatchChangeRequestsJob;
  refetch: () => void;
  mode: "peek" | "page";
  initialTab: "info" | "changeRequests";
  recordComposerDefinitionId?: string;
  onOpenPeek?: (target: DipPeekTarget) => void;
  canExecuteMutation: () => boolean;
}) {
  const { t } = useTranslation();
  const { isReadOnly } = useDipLifecycle();
  const [tab, setTab] = useState(initialTab);
  const [openEditForm, setOpenEditForm] = useState(false);
  const [logDialogJobRun, setLogDialogJobRun] = useState<JobRun | null>(null);
  const [dryRunJobRunId, setDryRunJobRunId] = useState<string | null>(null);
  const [openDryRunOptionsDialog, setOpenDryRunOptionsDialog] = useState(false);
  const [openRunJobDialog, setOpenRunJobDialog] = useState(false);
  useEffect(() => {
    if (!isReadOnly) return;
    setOpenEditForm(false);
    setOpenDryRunOptionsDialog(false);
    setOpenRunJobDialog(false);
  }, [isReadOnly]);
  const editEvidenceSetId = evidenceSetsOfBatchChangeRequestsJob(job)[0]?.id;
  const { markJobRunAsFailed, onActivateClick, onDeactivateClick } = useBatchChangeRequestsJobActions({
    refetch,
    evidenceSetId: editEvidenceSetId ?? "",
    canExecute: canExecuteMutation,
  });
  const { submit, processing } = useSubmitBatchChangeRequestsJob({
    jobId: job.id,
    refetch,
    setDryRunJobRunId,
    setLogDialogJobRun,
    jobName: "BatchChangeRequestsJob",
    skipConfirm: true,
  });

  const handleDryRunConfirm = (options: DryRunOptions, logLevel?: JobLogLevel) => {
    if (!canExecuteMutation()) return;
    submit(true, options, logLevel);
  };

  return (
    <div className={cn("space-y-5", mode === "page" && "mx-auto max-w-[var(--content-max)] p-8")}>
      <section className="space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className={cn("text-fg-1", mode === "page" ? "text-dewey-title" : "text-[18px] font-semibold")}>
                {job.name || `(${t("ui.noName")})`}
              </h2>
              {job.deactivatedAt ? <Badge tone="status-draft">{t("ui.deactivated")}</Badge> : null}
            </div>
            <dl className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-12-regular text-fg-3">
              <div className="flex gap-1.5">
                <dt>{t("model.common.id")}:</dt>
                <dd className="font-mono">{job.id}</dd>
              </div>
              <div className="flex gap-1.5">
                <dt>{t("model.common.createdAt")}:</dt>
                <dd>{formatDate(t, job.createdAt)}</dd>
              </div>
              <div className="flex gap-1.5">
                <dt>{t("model.common.updatedAt")}:</dt>
                <dd>{formatDate(t, job.updatedAt)}</dd>
              </div>
            </dl>
          </div>

          <div className="flex flex-wrap justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label={t("ui.refresh")}
              onClick={() => void refetch()}
            >
              <RefreshCw className="size-4" />
            </Button>
            <DipReadOnlyTooltip disabled={isReadOnly} ariaLabel={t("ui.dryRun")}>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={processing.value || isReadOnly}
                onClick={() => {
                  if (!canExecuteMutation()) return;
                  setOpenDryRunOptionsDialog(true);
                }}
              >
                <FlaskConical className="size-3.5" />
                {processing.value && processing.dryRun ? t("ui.processing") : t("ui.dryRun")}
              </Button>
            </DipReadOnlyTooltip>
            <DipReadOnlyTooltip
              disabled={isReadOnly}
              ariaLabel={t("feature.dataImportProjects.detail.operation.runJob")}
            >
              <Button
                type="button"
                size="sm"
                disabled={processing.value || isReadOnly}
                onClick={() => {
                  if (!canExecuteMutation()) return;
                  setOpenRunJobDialog(true);
                }}
              >
                <Play className="size-3.5" />
                {processing.value && !processing.dryRun
                  ? t("ui.processing")
                  : t("feature.dataImportProjects.detail.operation.runJob")}
              </Button>
            </DipReadOnlyTooltip>
            <DipReadOnlyTooltip disabled={isReadOnly} ariaLabel={t("ui.edit")}>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={processing.value || !editEvidenceSetId || isReadOnly}
                onClick={() => {
                  if (!canExecuteMutation()) return;
                  setOpenEditForm(true);
                }}
              >
                <Pencil className="size-3.5" />
                {t("ui.edit")}
              </Button>
            </DipReadOnlyTooltip>
            <DropdownMenu>
              <DipReadOnlyTooltip disabled={isReadOnly} ariaLabel={t("ui.operation", { defaultValue: "Operation" })}>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    disabled={isReadOnly}
                    aria-label={t("ui.operation", { defaultValue: "Operation" })}
                  >
                    <MoreHorizontal className="size-4" />
                  </Button>
                </DropdownMenuTrigger>
              </DipReadOnlyTooltip>
              <DropdownMenuContent align="end">
                {job.deactivatedAt ? (
                  <DropdownMenuItem
                    onClick={() => {
                      if (!canExecuteMutation()) return;
                      onActivateClick({ id: job.id, name: job.name ?? "this change requests job" });
                    }}
                  >
                    {t("ui.activate")}
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem
                    onClick={() => {
                      if (!canExecuteMutation()) return;
                      onDeactivateClick({ id: job.id, name: job.name ?? "this change requests job" });
                    }}
                  >
                    {t("ui.deactivate")}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </section>

      <Tabs value={tab} onValueChange={(value) => setTab(value as "info" | "changeRequests")}>
        <TabsList>
          <TabsTrigger value="info">{t("ui.info")}</TabsTrigger>
          <TabsTrigger value="changeRequests">{t("model.changeRequest.modelNames")}</TabsTrigger>
        </TabsList>
        <TabsContent value="info" className="space-y-5 pt-4">
          <BatchChangeRequestsJobInfo job={job} onOpenPeek={onOpenPeek} />
          <section className="space-y-3">
            <h3 className="text-14-bold text-fg-1">{t("ui.history")}</h3>
            <JobRunsTable
              job={job}
              onOpenLog={setLogDialogJobRun}
              onMarkFailed={(jobRunId) => {
                if (!canExecuteMutation()) return;
                markJobRunAsFailed({ jobRunId });
              }}
              readOnly={isReadOnly}
            />
          </section>
        </TabsContent>
        <TabsContent value="changeRequests" className="pt-4">
          <BatchChangeRequestsPanel
            jobId={job.id}
            jobName={job.name ?? null}
            onOpenPeek={onOpenPeek}
            jobRuns={job.batchChangeRequestsJobRuns}
            recordComposerDefinitionId={recordComposerDefinitionId}
            onOpenLog={setLogDialogJobRun}
            canExecuteMutation={canExecuteMutation}
          />
        </TabsContent>
      </Tabs>

      {editEvidenceSetId && !isReadOnly ? (
        <BatchChangeRequestsJobEditBridge
          open={openEditForm && !isReadOnly}
          onOpenChange={(open) => {
            if (!open || canExecuteMutation()) setOpenEditForm(open);
          }}
          job={job}
          importProjectId={job.dataImportProjectId}
          evidenceSetId={editEvidenceSetId}
        />
      ) : null}
      <DryRunOptionsDialog
        open={openDryRunOptionsDialog && !isReadOnly}
        onOpenChange={(open) => {
          if (!open || canExecuteMutation()) setOpenDryRunOptionsDialog(open);
        }}
        onConfirm={handleDryRunConfirm}
        readOnly={isReadOnly}
      />
      <RunJobDialog
        open={openRunJobDialog && !isReadOnly}
        onOpenChange={(open) => {
          if (!open || canExecuteMutation()) setOpenRunJobDialog(open);
        }}
        onConfirm={(logLevel) => {
          if (!canExecuteMutation()) return;
          submit(false, undefined, logLevel);
        }}
        readOnly={isReadOnly}
      />
      <BatchChangeRequestsJobLogDialog
        jobRun={logDialogJobRun}
        onOpenChange={(open) => {
          if (!open) {
            refetch();
            setLogDialogJobRun(null);
          }
        }}
        onFetchResult={() => setDryRunJobRunId(logDialogJobRun?.jobRunId ?? null)}
      />
      <BatchChangeRequestsJobDryRunResultDialog
        open={dryRunJobRunId !== null}
        onOpenChange={(open) => !open && setDryRunJobRunId(null)}
        jobRunId={dryRunJobRunId}
        jobId={job.id}
      />
    </div>
  );
}

export function BatchChangeRequestsJobDetailPanel({
  jobId,
  mode = "peek",
  initialTab = "info",
  recordComposerDefinitionId,
  onOpenPeek,
}: BatchChangeRequestsJobDetailPanelProps) {
  const { t } = useTranslation();
  const { projectId } = useDipLifecycle();
  const { canExecuteMutation, setAuthorized } = useDipResourceMutationGuard(`batch-change-requests-job:${jobId}`);
  const { tenantId } = useTenant();
  if (!tenantId) {
    throw new Error("Tenant ID is required");
  }
  const { data, refetch } = useSuspenseQuery<GetBatchChangeRequestsJobQuery, GetBatchChangeRequestsJobQueryVariables>(
    GetBatchChangeRequestsJobDocument,
    jobId ? { variables: { tenantId, id: jobId } } : skipToken,
  );
  const job = data?.batchChangeRequestsJobs[0];
  const isAuthorized = job?.dataImportProjectId === projectId;
  setAuthorized(isAuthorized);

  if (!job || !isAuthorized) {
    return <p className="text-12-regular text-fg-3">{t("feature.common.noData")}</p>;
  }

  return (
    <BatchChangeRequestsJobDetailPanelContent
      job={job}
      refetch={() => void refetch()}
      mode={mode}
      initialTab={initialTab}
      recordComposerDefinitionId={recordComposerDefinitionId}
      onOpenPeek={onOpenPeek}
      canExecuteMutation={canExecuteMutation}
    />
  );
}
